import { Component, h } from 'preact'
import Header from '../header'
import AlertComponent from '../ui-components/AlertComponent'
import AccordionPanel from '../panel/AccordionPanel'
import { CloseInactiveTopicsPanelBody, CloseInactiveTopicsPanelHeader } from './CloseInactiveTopicsPanel'
import axios from 'axios'
import Loader from '../ui-components/Loader'

interface AutomationRulesProps {
    closeInactiveTopicsEnabled: boolean,
    selectedCategoryList: Array<string>,
    closeTopicsAfter?: number
}
interface Props extends AutomationRulesProps{
    categories: any[]
}

interface State {
    storedState: AutomationRulesProps,
    isCloseInactiveTopicsEnabled: boolean,
    selectedCategories: Array<string>,
    closeTopicsIn?: number,
    showCloseInactiveTopicsActionButtons: boolean,
    showCategoryDropdown: boolean,
    isLoading: boolean,

    showValidationError: boolean,
    hasUnsavedChanges: boolean,
    showUnsavedChangesModal: boolean,

    messageType?: string|null,
    message?: string,
}

class AutomationRules extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      storedState: {
        closeInactiveTopicsEnabled: this.props.closeInactiveTopicsEnabled,
        selectedCategoryList: this.props.selectedCategoryList,
        closeTopicsAfter: this.props.closeTopicsAfter === 0 ? undefined : this.props.closeTopicsAfter
      },
      isCloseInactiveTopicsEnabled: this.props.closeInactiveTopicsEnabled,
      closeTopicsIn: this.props.closeTopicsAfter === 0 ? undefined : this.props.closeTopicsAfter,
      selectedCategories: this.props.selectedCategoryList,
      showCloseInactiveTopicsActionButtons: false,
      showCategoryDropdown: false,
      showValidationError: false,
      hasUnsavedChanges: false,
      showUnsavedChangesModal: false,
      isLoading: false
    }
    this.saveCloseInactiveTopicsRule = this.saveCloseInactiveTopicsRule.bind(this)
    this.resetCloseInactiveTopicsSetting = this.resetCloseInactiveTopicsSetting.bind(this)
    this.onCloseInactiveTopicsSettingChanged = this.onCloseInactiveTopicsSettingChanged.bind(this)
    this.onInputChange = this.onInputChange.bind(this)
    this.onInputFocus = this.onInputFocus.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.toggleCategoryDropdown = this.toggleCategoryDropdown.bind(this)
    this.onResetCategories = this.onResetCategories.bind(this)
    this.onSelectedCategoriesChanged = this.onSelectedCategoriesChanged.bind(this)
  }

  async saveCloseInactiveTopicsRule () {
    const { closeTopicsIn } = this.state
    let invalidInputForCloseTopicsIn = closeTopicsIn === undefined || closeTopicsIn === null || closeTopicsIn <= 0
    if (invalidInputForCloseTopicsIn && this.state.isCloseInactiveTopicsEnabled === true) {
      this.setState({ showValidationError: true })
      return
    }
    this.setState({ messageType: null, isLoading: true })
    const successMessage = `'Automatically close inactive topics' settings have been updated successfully.`
    const errorMessage = 'Something went wrong, settings can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/automationRules/closeInactiveTopicsSetting',
        {
          isCloseInactiveTopicsEnabled: this.state.isCloseInactiveTopicsEnabled,
          closeTopicsAfterDays: this.state.closeTopicsIn ?? 0,
          selectedCategories: this.state.selectedCategories
        })
      const updatedState = this.state.storedState
      updatedState.closeTopicsAfter = this.state.closeTopicsIn
      updatedState.selectedCategoryList = this.state.selectedCategories
      updatedState.closeInactiveTopicsEnabled = this.state.isCloseInactiveTopicsEnabled
      this.setState({
        isLoading: false,
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showCloseInactiveTopicsActionButtons: false
      })
    } catch {
      this.setState({
        isLoading: false,
        messageType: 'error',
        message: errorMessage,
        showCloseInactiveTopicsActionButtons: false
      })
    }
  }

  resetCloseInactiveTopicsSetting () {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    this.setState({
      isCloseInactiveTopicsEnabled: closeInactiveTopicsEnabled,
      closeTopicsIn: closeTopicsAfter,
      selectedCategories: selectedCategoryList,
      showCloseInactiveTopicsActionButtons: false,
      showValidationError: false
    })
  }

  onCloseInactiveTopicsSettingChanged (value: boolean) {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    let showActionButtons = closeInactiveTopicsEnabled !== value || closeTopicsAfter !== this.state.closeTopicsIn || selectedCategoryList !== this.state.selectedCategories
    if (!value) {
      this.setState({ showValidationError: false })
    }
    this.setState({
      isCloseInactiveTopicsEnabled: value,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      showCategoryDropdown: false,
      hasUnsavedChanges: showActionButtons
    })
  }

  onInputChange (value: number) {
    if (isNaN(value) || value <= 0) {
      this.setState({
        closeTopicsIn: undefined,
        showValidationError: true,
        hasUnsavedChanges: true,
        showCloseInactiveTopicsActionButtons: true
      })
      return
    }
    let showActionButtons = this.state.storedState.closeTopicsAfter !== value
    this.setState({
      closeTopicsIn: value,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showValidationError: false
    })
  }

  onInputFocus () {
    this.setState({
      showCloseInactiveTopicsActionButtons: true,
      showValidationError: false,
      showCategoryDropdown: false
    })
  }

  onInputBlur () {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    const showActionButtons = closeInactiveTopicsEnabled !== this.state.isCloseInactiveTopicsEnabled || closeTopicsAfter !== this.state.closeTopicsIn || selectedCategoryList !== this.state.selectedCategories
    this.setState({
      showCloseInactiveTopicsActionButtons: showActionButtons
    })
  }

  toggleCategoryDropdown () {
    this.setState({
      showCategoryDropdown: !this.state.showCategoryDropdown,
      showCloseInactiveTopicsActionButtons: this.state.showCloseInactiveTopicsActionButtons === true ? !this.state.showCloseInactiveTopicsActionButtons : this.state.showCloseInactiveTopicsActionButtons
    })
  }

  onSelectedCategoriesChanged (id: string, data: Array<string>) {
    let showActionButtons = this.state.storedState.selectedCategoryList !== data

    this.setState({
      selectedCategories: data,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showCategoryDropdown: false
    })
  }

  onResetCategories () {
    this.setState({
      showCategoryDropdown: false
    })
  }

  normalizeCategories (categories: any[]) {
    return categories
      .filter(parent => parent.categories)
      .map(parent => {
        return {
          parentId: parent.groupId,
          parentName: parent.groupName,
          items: parent.categories.map((child: { categoryName: any; categoryId: any }, idx: any) => {
            return {
              id: idx,
              name: child.categoryName,
              value: child.categoryId
            }
          })
        }
      })
  }

  render ({ categories }: Props, { storedState, selectedCategories, closeTopicsIn, isCloseInactiveTopicsEnabled, showCloseInactiveTopicsActionButtons, showValidationError, message, messageType, showUnsavedChangesModal, showCategoryDropdown, isLoading } : State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const closeInactiveTopicsStatusText = storedState.closeInactiveTopicsEnabled ? 'on' : 'off'
    const categoryDropdownId = 'categoryDropdownFilter'

    const CloseInactiveTopicsPanel = {
      title: <CloseInactiveTopicsPanelHeader statusText={closeInactiveTopicsStatusText} />,
      body: <CloseInactiveTopicsPanelBody
        isCloseInactiveTopicsEnabled={isCloseInactiveTopicsEnabled}
        selectedCategories={selectedCategories}
        categories={this.normalizeCategories(categories)}
        closeTopicsIn={closeTopicsIn}
        showCategoryDropdown={showCategoryDropdown}
        showActionButtons={showCloseInactiveTopicsActionButtons}
        showValidationError={showValidationError}
        onSave={() => this.saveCloseInactiveTopicsRule()}
        onCancel={() => this.resetCloseInactiveTopicsSetting()}
        onResetCategories={() => this.onResetCategories()}
        onSelectedCategoriesChanged={
          (categoryDropdownId: string, selectedCategories: Array<string>) => this.onSelectedCategoriesChanged(categoryDropdownId, selectedCategories)
        }
        categoryDropdownId={categoryDropdownId}
        onInputChange={(value: string) => this.onInputChange(parseInt(value))}
        onInputFocus={() => this.onInputFocus()}
        onInputBlur={() => this.onInputBlur()}
        toggleCategoryDropdown={() => this.toggleCategoryDropdown()}
        onCloseInactiveTopicsSettingChanged={() => this.onCloseInactiveTopicsSettingChanged(!isCloseInactiveTopicsEnabled)} />
    }
    return (
      <div className='automation-rules-overview qa-automation-rules-overview'>
        <Header
          title='Automation Rules'
          disableCancelButton
          disableSaveButton
        />
        {isLoading && <Loader />}
        {
          messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
        }
        <AccordionPanel
          panels={[CloseInactiveTopicsPanel]}
          renderUnsavedChangesModal={() => this.setState({ showUnsavedChangesModal: true })}
        />
      </div>
    )
  }
}
export default AutomationRules
